import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatPrice, getImageUrl } from '@/lib/utils';
import { useState } from 'react';
import ImageZoomModal from '@/components/ImageZoomModal';

export function ProductCard({ product, onAddToCart }) {
  const [isHovered, setIsHovered] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const discount = product.compare_at_price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  const isLowStock = product.inventory < 10 && product.inventory > 0;
  const isSoldOut = product.inventory === 0;

  const handleZoomClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoomOpen(true);
  };

  return (
    <div
      className="product-card group relative flex flex-col gap-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image */}
      <Link to={`/product/${product.product_id}`} className="relative">
        <div className="image-zoom-wrapper aspect-[3/4] bg-stone-100 relative group/image">
          <img
            src={getImageUrl(product.images[0]) || 'https://via.placeholder.com/600x800'}
            alt={product.name}
            className="image-zoom w-full h-full object-cover"
            data-testid="product-image"
          />
          {isSoldOut && (
            <div className="absolute inset-0 bg-white/90 flex items-center justify-center">
              <Badge variant="destructive" className="text-base px-6 py-2">
                Sold Out
              </Badge>
            </div>
          )}
          
          {/* Zoom Button Overlay */}
          {!isSoldOut && (
            <button
              onClick={handleZoomClick}
              className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover/image:opacity-100 transition-opacity shadow-md hover:bg-white"
              data-testid="zoom-button"
            >
              <ZoomIn className="h-4 w-4 text-stone-700" />
            </button>
          )}
        </div>

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {discount > 0 && !isSoldOut && (
            <Badge className="bg-rose-700 text-white" data-testid="discount-badge">
              -{discount}%
            </Badge>
          )}
          {product.featured && (
            <Badge className="bg-stone-900 text-white">Featured</Badge>
          )}
        </div>

        {/* Wishlist Button */}
        <Button
          variant="ghost"
          size="icon"
          className={`absolute top-4 right-4 rounded-full bg-white/90 backdrop-blur-sm transition-opacity ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid="wishlist-button"
        >
          <Heart className="h-5 w-5" />
        </Button>
      </Link>

      {/* Info */}
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/product/${product.product_id}`} className="flex-1">
            <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">
              {product.collection}
            </p>
            <h3 className="font-light text-base leading-tight text-stone-900 group-hover:underline"
              data-testid="product-name">
              {product.name}
            </h3>
          </Link>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-lg font-light text-stone-900" data-testid="product-price">
            {formatPrice(product.price)}
          </span>
          {product.compare_at_price && (
            <span className="text-sm text-stone-400 line-through" data-testid="compare-price">
              {formatPrice(product.compare_at_price)}
            </span>
          )}
        </div>

        <p className="text-sm text-stone-600 line-clamp-2 leading-relaxed">
          {product.material}
        </p>

        {/* Add to Cart Button */}
        <Button
          onClick={(e) => {
            e.preventDefault();
            onAddToCart && onAddToCart(product);
          }}
          disabled={isSoldOut}
          className={`w-full mt-2 btn-primary transition-all duration-300 ${
            isHovered && !isSoldOut ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          } md:opacity-0 md:group-hover:opacity-100 md:group-hover:translate-y-0`}
          data-testid="add-to-cart-button"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          {isSoldOut ? 'Sold Out' : 'Add to Cart'}
        </Button>
      </div>

      {/* Image Zoom Modal */}
      <ImageZoomModal
        images={product.images}
        initialIndex={0}
        isOpen={zoomOpen}
        onClose={() => setZoomOpen(false)}
        productName={product.name}
      />
    </div>
  );
}